<template>
  <div
    id="header"
    class="bg-primary flex h-[70px] items-center px-4 sticky text-white w-full z-20"
    data-test="header">
    <IconBase
      aria-controls="side-menu-navigation"
      :aria-expanded="isMobileSidebarVisible"
      :aria-label="t('ui.navigation.main')"
      class="block cursor-pointer lg:hidden min-w-[32px] ml-n3 mr-2 pr-2"
      role="button"
      :size="IconSize.MediumLarge"
      @click="openMobileSidebar">
      <Hamburger />
    </IconBase>

    <a href="/">
      <BaseImage
        alt="Myparcel"
        class="block h-6 md:hidden min-w-[24px]"
        :src="proposition.assets.logo.small.url" />

      <BaseImage
        alt="Myparcel"
        class="h-6 hidden md:block min-w-[147px]"
        :src="proposition.assets.logo.medium.url" />
    </a>

    <MenuButton
      :button-text="currentShop.name"
      class="ml-4 min-w-0 max-w-[150px] md:max-w-none"
      name="preferred-shop"
      :options="shopOptions" />

    <div class="flex gap-5 items-center ml-auto">
      <HotjarFeedback v-if="proposition.tools.hotjar.enableFeedbackModal" />

      <AppCuesLaunchpad v-if="configStore.appcuesIsReady" />

      <NewShipmentDropDown />
    </div>
  </div>
</template>

<script setup lang="ts">
import type {MenuButtonOption} from '@myparcel-frontend/ui';
import {IconBase} from '@myparcel-frontend/icons/legacy';
import {useProposition} from '@myparcel-frontend/proposition';
import {Hamburger, IconSize, BaseImage, MenuButton} from '@myparcel-frontend/ui';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import HotjarFeedback from './HotjarFeedback.vue';
import AppCuesLaunchpad from '@/components/ui/AppCuesLaunchpad/AppCuesLaunchpad.vue';
import NewShipmentDropDown from '@/components/ui/NewShipmentDropDown/NewShipmentDropDown.vue';
import useAccount from '@/composables/useAccount/useAccount';
import useShop from '@/composables/useShop/useShop';
import useSidebarToggle from '@/composables/useSidebarToggle';
import useUiSettings from '@/composables/useUiSettings';
import {useConfigStore} from '@/store/config';

const {proposition} = useProposition();
const {openMobileSidebar, isMobileSidebarVisible} = useSidebarToggle();
const {uiSettings, putUiSettings} = useUiSettings();
const {activeShops} = useAccount();
const {currentShop} = useShop();
const configStore = useConfigStore();
const router = useRouter();
const {t} = useI18n();

const shopOptions = computed<MenuButtonOption[]>(() => {
  return activeSortedShops.value.map((shop) => ({
    label: shop.name,
    key: shop.id.toString(),
    active: shop.id === currentShop.value.id,
    onClick: () => changeCurrentShop(shop.id),
  }));
});

const sortShopsAsc = () =>
  activeShops.value.sort((shopA, shopB) => {
    return shopA.name.localeCompare(shopB.name);
  });

const activeSortedShops = computed(() => sortShopsAsc());

const hasShopIdInTheCurrentRoute = () => {
  return Boolean(router.currentRoute.value.params?.shopId);
};

const updateRouteWithNewShopId = async (shopId: number): Promise<void> => {
  const route = router.currentRoute.value;
  const newRoute = {
    ...route,
    params: {
      ...route.params,
      shopId: shopId.toString(),
    },
  };

  await router.push(newRoute);
};

const changeCurrentShop = async (shopId: number): Promise<void> => {
  if (shopId === currentShop.value.id) {
    return;
  }

  await putUiSettings({
    ...uiSettings.value,
    preferred_shop_id: shopId,
  });

  if (hasShopIdInTheCurrentRoute()) {
    await updateRouteWithNewShopId(shopId);
  }
};
</script>
